import { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { RefreshCw, AlertTriangle, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { getCallQuality, syncCallQuality } from '../services/portalApi';

function fmtDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const str = iso.includes('T') ? iso : iso.replace(' ', 'T');
    const d = new Date(str.endsWith('Z') || str.includes('+') ? str : str + 'Z');
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: 'var(--muted)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toFixed ? p.value.toFixed(2) : p.value : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

const QUALITY_CFG = {
  good:    { color: 'var(--ok)', bg: 'var(--ok-bg)', border: '#14532d44', label: 'Good',    Icon: CheckCircle },
  fair:    { color: 'var(--warn)', bg: 'var(--warn-bg)', border: '#92400e44', label: 'Fair',    Icon: AlertCircle },
  poor:    { color: 'var(--err)', bg: 'var(--err-bg)', border: 'var(--err-border)', label: 'Poor',    Icon: XCircle },
  unknown: { color: 'var(--muted)', bg: 'var(--border)', border: 'var(--border-strong)',   label: 'Unknown', Icon: null },
};

function QualityBadge({ label }) {
  const cfg = QUALITY_CFG[label] || QUALITY_CFG.unknown;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}

function MosBadge({ mos }) {
  if (mos == null) return <span style={{ color: 'var(--muted-3)' }}>—</span>;
  const color = mos >= 4.0 ? 'var(--ok)' : mos >= 3.5 ? 'var(--warn)' : 'var(--err)';
  return <span style={{ fontFamily: 'monospace', fontSize: 12, color, fontWeight: 700 }}>{mos.toFixed(2)}</span>;
}

const PAGE_SIZE = 50;
const QUALITY_FILTERS = ['all', 'good', 'fair', 'poor', 'unknown'];

export default function TeamsCallQuality({ user, viewAsCustomer }) {
  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [qualFilter,   setQualFilter]   = useState('all');
  const [page,         setPage]         = useState(0);

  const effectiveCid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);
  const needsCustomer = user?.isAdmin && !viewAsCustomer;

  const load = useCallback(async () => {
    if (!effectiveCid) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await getCallQuality(effectiveCid, 30);
      setRecords(data.records || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [effectiveCid]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    if (!effectiveCid) return;
    setSyncing(true);
    setError(null);
    try {
      await syncCallQuality(effectiveCid);
      await load();
    } catch (e) {
      if (e.message?.includes('403') || e.message?.includes('Missing')) {
        setError('Missing permission: CallRecords.Read.All — add this Application permission to the Azure AD app registration and grant admin consent.');
      } else {
        setError(e.message);
      }
    } finally {
      setSyncing(false);
    }
  };

  // ── Stats ───────────────────────────────────────────────────────────────────
  const withMos    = records.filter(r => r.avg_mos != null);
  const avgMos     = withMos.length ? withMos.reduce((s, r) => s + r.avg_mos, 0) / withMos.length : null;
  const goodCnt    = records.filter(r => r.quality_label === 'good').length;
  const fairCnt    = records.filter(r => r.quality_label === 'fair').length;
  const poorCnt    = records.filter(r => r.quality_label === 'poor').length;
  const poorRate   = records.length ? ((poorCnt / records.length) * 100) : null;
  const avgPl      = withMos.length ? withMos.reduce((s, r) => s + (r.avg_packet_loss || 0), 0) / withMos.length : null;
  const avgJitter  = withMos.filter(r => r.avg_jitter_ms != null).length
    ? withMos.filter(r => r.avg_jitter_ms != null).reduce((s, r) => s + r.avg_jitter_ms, 0) / withMos.filter(r => r.avg_jitter_ms != null).length
    : null;

  // ── MOS trend ──────────────────────────────────────────────────────────────
  const mosDayMap = {};
  for (const r of records) {
    if (!r.start_time || r.avg_mos == null) continue;
    const day = r.start_time.slice(0, 10);
    if (!mosDayMap[day]) mosDayMap[day] = { day, total: 0, count: 0, poor: 0 };
    mosDayMap[day].total += r.avg_mos;
    mosDayMap[day].count++;
    if (r.quality_label === 'poor') mosDayMap[day].poor++;
  }
  const mosTrend = Object.values(mosDayMap)
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(-14)
    .map(d => ({
      day:  new Date(d.day + 'T12:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      mos:  Math.round(d.total / d.count * 100) / 100,
      poor: d.poor,
    }));

  // ── Quality distribution per day ───────────────────────────────────────────
  const distDayMap = {};
  for (const r of records) {
    if (!r.start_time) continue;
    const day = r.start_time.slice(0, 10);
    if (!distDayMap[day]) distDayMap[day] = { day, good: 0, fair: 0, poor: 0 };
    if (r.quality_label === 'good') distDayMap[day].good++;
    else if (r.quality_label === 'fair') distDayMap[day].fair++;
    else if (r.quality_label === 'poor') distDayMap[day].poor++;
  }
  const distTrend = Object.values(distDayMap)
    .sort((a, b) => a.day.localeCompare(b.day))
    .slice(-14)
    .map(d => ({
      day:  new Date(d.day + 'T12:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      Good: d.good, Fair: d.fair, Poor: d.poor,
    }));

  // ── Filtered table ─────────────────────────────────────────────────────────
  const filtered = records.filter(r => {
    if (qualFilter !== 'all' && (r.quality_label || 'unknown') !== qualFilter) return false;
    if (!search) return true;
    return (
      r.organizer_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.organizer_upn?.toLowerCase().includes(search.toLowerCase())
    );
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows  = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (needsCustomer) {
    return (
      <div className="content">
        <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '80px 0', fontSize: 14 }}>
          Select a customer to view Teams call quality data.
        </div>
      </div>
    );
  }

  return (
    <div className="content">

      {/* Header action row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--muted-2)' }}>
            Call quality data from Microsoft Graph Call Records API · last 30 days
          </div>
        </div>
        {user?.isAdmin && (
          <button className="btn btn-sm" onClick={handleSync} disabled={syncing || loading}>
            {syncing
              ? <><RefreshCw size={11} style={{ animation: 'spin 0.8s linear infinite', marginRight: 4 }}/>Syncing…</>
              : 'Sync from M365'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
          <AlertTriangle size={16} color="var(--err)" style={{ flexShrink: 0, marginTop: 1 }}/>
          <div>
            <div style={{ fontSize: 13, color: 'var(--err)', fontWeight: 600, marginBottom: 4 }}>Error</div>
            <div style={{ fontSize: 12, color: 'var(--err)' }}>{error}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '80px 0' }}>Loading call quality data…</div>
      ) : records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ color: 'var(--muted-3)', fontSize: 14, marginBottom: 8 }}>No call quality data yet.</div>
          <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>
            {user?.isAdmin
              ? <>Click <strong style={{ color: 'var(--info)' }}>Sync from M365</strong> to pull call records from the Microsoft 365 tenant. Requires the <code style={{ color: 'var(--purple)' }}>CallRecords.Read.All</code> permission.</>
              : 'Call quality data will appear here once your administrator syncs records from Microsoft 365.'}
          </div>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="stats-grid mb-20">
            {[
              { label: 'Total Calls', value: records.length.toLocaleString() },
              {
                label: 'Average MOS',
                value: avgMos != null ? avgMos.toFixed(2) : '—',
                color: avgMos != null ? (avgMos >= 4.0 ? 'var(--ok)' : avgMos >= 3.5 ? 'var(--warn)' : 'var(--err)') : undefined,
              },
              {
                label: 'Poor Call Rate',
                value: poorRate != null ? `${poorRate.toFixed(1)}%` : '—',
                color: poorRate != null ? (poorRate < 5 ? 'var(--ok)' : poorRate < 10 ? 'var(--warn)' : 'var(--err)') : undefined,
              },
              { label: 'Avg Packet Loss', value: avgPl != null ? `${(avgPl * 100).toFixed(2)}%` : '—' },
              { label: 'Avg Jitter', value: avgJitter != null ? `${avgJitter.toFixed(0)}ms` : '—' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={s.color ? { color: s.color } : undefined}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Quality summary badges */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {[
              { key: 'good', cnt: goodCnt,  label: 'Good',    color: 'var(--ok)', bg: 'var(--ok-bg)', border: '#14532d44' },
              { key: 'fair', cnt: fairCnt,  label: 'Fair',    color: 'var(--warn)', bg: 'var(--warn-bg)', border: '#92400e44' },
              { key: 'poor', cnt: poorCnt,  label: 'Poor',    color: 'var(--err)', bg: 'var(--err-bg)', border: 'var(--err-border)' },
            ].map(q => (
              <div key={q.key} style={{ flex: 1, background: q.bg, border: `1px solid ${q.border}`, borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 11, color: q.color, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{q.label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: q.color }}>{q.cnt}</div>
                <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                  {records.length ? `${((q.cnt / records.length) * 100).toFixed(1)}% of calls` : ''}
                </div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

            {/* MOS trend */}
            <div className="card">
              <div className="card-header"><div className="card-title">Daily Average MOS Score</div></div>
              <div className="card-body" style={{ paddingTop: 8 }}>
                {mosTrend.length < 2 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '30px 0', fontSize: 12 }}>Not enough data for a trend chart.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={mosTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                      <XAxis dataKey="day" tick={{ fill: 'var(--muted-3)', fontSize: 10 }} axisLine={false} tickLine={false}/>
                      <YAxis domain={[2.5, 5]} tick={{ fill: 'var(--muted-3)', fontSize: 10 }} axisLine={false} tickLine={false}/>
                      <Tooltip content={<CustomTooltip/>} cursor={{ stroke: 'var(--border-strong)' }}/>
                      <Line type="monotone" dataKey="mos" name="Avg MOS" stroke="var(--purple)" strokeWidth={2} dot={{ fill: 'var(--purple)', r: 3 }} activeDot={{ r: 5 }}/>
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Quality distribution per day */}
            <div className="card">
              <div className="card-header"><div className="card-title">Daily Quality Distribution</div></div>
              <div className="card-body" style={{ paddingTop: 8 }}>
                {distTrend.length < 2 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '30px 0', fontSize: 12 }}>Not enough data for a trend chart.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={distTrend} barGap={1}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                      <XAxis dataKey="day" tick={{ fill: 'var(--muted-3)', fontSize: 10 }} axisLine={false} tickLine={false}/>
                      <YAxis tick={{ fill: 'var(--muted-3)', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false}/>
                      <Tooltip content={<CustomTooltip/>} cursor={{ fill: '#ffffff08' }}/>
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: 'var(--muted-2)' }}/>
                      <Bar dataKey="Good" stackId="q" fill="var(--ok)" radius={[0,0,0,0]}/>
                      <Bar dataKey="Fair" stackId="q" fill="var(--warn)" radius={[0,0,0,0]}/>
                      <Bar dataKey="Poor" stackId="q" fill="var(--err)" radius={[3,3,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>

          {/* Call detail table */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Call Detail</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Quality filter pills */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {QUALITY_FILTERS.map(f => {
                    const cfg = f === 'all' ? null : QUALITY_CFG[f];
                    const active = qualFilter === f;
                    return (
                      <button
                        key={f}
                        onClick={() => { setQualFilter(f); setPage(0); }}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, cursor: 'pointer',
                          background: active ? (cfg?.bg || 'var(--border)') : 'transparent',
                          color: active ? (cfg?.color || 'var(--text-2)') : 'var(--muted-3)',
                          border: `1px solid ${active ? (cfg?.border || 'var(--border-strong)') : 'var(--border)'}`,
                        }}
                      >
                        {f === 'all' ? 'All' : (cfg?.label || f)}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  placeholder="Search organizer…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(0); }}
                  style={{
                    background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
                    color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, width: 200,
                  }}
                />
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date / Time</th>
                    <th>Type</th>
                    <th>Organizer</th>
                    <th>Participants</th>
                    <th>Duration</th>
                    <th>Modalities</th>
                    <th>MOS</th>
                    <th>Packet Loss</th>
                    <th>Jitter</th>
                    <th>Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>No calls match the current filter.</td></tr>
                  ) : pageRows.map((r, i) => {
                    let mods = [];
                    try { mods = JSON.parse(r.modalities || '[]'); } catch {}
                    return (
                      <tr key={r.id || i}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(r.start_time)}</td>
                        <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {r.call_type === 'peerToPeer' ? 'P2P' : r.call_type === 'groupCall' ? 'Group' : r.call_type || '—'}
                        </td>
                        <td>
                          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.organizer_name || '—'}</div>
                          {r.organizer_upn && <div style={{ fontSize: 10, color: 'var(--muted-3)' }}>{r.organizer_upn}</div>}
                        </td>
                        <td style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>{r.participant_cnt || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{fmtDuration(r.duration_sec)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {mods.map(m => (
                              <span key={m} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'var(--border)', color: 'var(--muted)', border: '1px solid var(--border-strong)' }}>{m}</span>
                            ))}
                          </div>
                        </td>
                        <td><MosBadge mos={r.avg_mos}/></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: r.avg_packet_loss > 0.05 ? 'var(--err)' : r.avg_packet_loss > 0.01 ? 'var(--warn)' : 'var(--muted)' }}>
                          {r.avg_packet_loss != null ? `${(r.avg_packet_loss * 100).toFixed(2)}%` : '—'}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: r.avg_jitter_ms > 30 ? 'var(--err)' : 'var(--muted)' }}>
                          {r.avg_jitter_ms != null ? `${r.avg_jitter_ms}ms` : '—'}
                        </td>
                        <td><QualityBadge label={r.quality_label}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: page === 0 ? 'default' : 'pointer' }}
                >←</button>
                <span style={{ color: 'var(--muted-3)' }}>Page {page + 1} of {pageCount}</span>
                <button
                  onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                  disabled={page === pageCount - 1}
                  style={{ background: 'none', border: '1px solid var(--border-strong)', color: 'var(--muted)', borderRadius: 4, padding: '3px 10px', cursor: page === pageCount - 1 ? 'default' : 'pointer' }}
                >→</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
